import { UserActivity, SYSTEM_ACCOUNTS, type CrewPostLinks } from '@watchlist/shared';
import { getProfileFromCache } from './profileCache';

// ─── Kind taxonomy ──────────────────────────────────────────────────────────
export type ActivityBundleKind =
  | 'movie_watched'      // watched / rated / reviewed
  | 'movie_added'        // added to a watchlist (want-to-watch)
  | 'watchlist_created'  // created a new public watchlist (not bundled — one row per watchlist)
  | 'crew_post';          // official Watchlist Crew post (moderator console)

const MOVIE_WATCHED_TYPES: string[] = [
  'movie_watched', 'movie_rated', 'movie_reviewed', 'marked_as_watched',
];
const MOVIE_ADDED_TYPES: string[] = [
  'movie_added_to_list', 'watchlist_add', 'added_to_watchlist',
];
const WATCHLIST_CREATED_TYPES: string[] = ['watchlist_created', 'created_watchlist'];

/** Official Watchlist Crew account — exclude its automated activity from the social feed */
export const WATCHLIST_CREW_UID = SYSTEM_ACCOUNTS.WATCHLIST_CREW_UID;

/** Classify a raw activity; return null to exclude it from the feed.
 * `user_followed` activities are intentionally excluded — they aren't social posts. */
export function getBundleKind(activity: UserActivity): ActivityBundleKind | null {
  if (activity.uid === WATCHLIST_CREW_UID) return null;
  if (activity.isPrivate) return null;
  const type = activity.type as string;
  if (MOVIE_WATCHED_TYPES.includes(type)) return 'movie_watched';
  if (MOVIE_ADDED_TYPES.includes(type))   return 'movie_added';
  if (WATCHLIST_CREATED_TYPES.includes(type)) {
    if (activity.metadata?.watchlistIsPrivate) return null;
    return 'watchlist_created';
  }
  return null;
}

// ─── Feed item ──────────────────────────────────────────────────────────────
export interface CrewFeedItem {
  id: string;
  kind: ActivityBundleKind;
  actorId: string;
  createdAt: number; // ms epoch of newest event in bundle
  isBundled: boolean;
  count: number;
  activities: UserActivity[];
  /** Present when kind === 'crew_post' */
  crewPost?: {
    title: string;
    subtitle?: string;
    links: CrewPostLinks;
    isPinned?: boolean;
    pinExpiresAtMs?: number;
    /** Firestore document ID of the crewPosts doc (not the feed item id which is `crewpost_${docId}`) */
    postDocId: string;
    /** UID of the user who created the post — used to show delete option to the author */
    authorUid?: string;
    likeCount?: number;
    likedBy?: string[];
    commentCount?: number;
  };
}

const BUNDLE_MIN = 4; // 1-3 → individual rows; 4+ → bundle strip

/** Activities before this date are ignored (older records lack complete metadata). */
export const ACTIVITY_CUTOFF_MS = new Date('2026-04-03T00:00:00.000Z').getTime();

/**
 * Filter, sort newest-first, and collapse same-actor/same-kind activities by calendar day.
 * Reviews are never bundled (each review gets its own row).
 * @param includeHidden - If true, include hidden activities (for HiddenFromCrewPage). Default false.
 */
export function bundleCrewItems(rawItems: UserActivity[], includeHidden: boolean = false): CrewFeedItem[] {
  const visible = rawItems
    .filter(a => a.uid !== WATCHLIST_CREW_UID)
    .filter(a => getBundleKind(a) !== null)
    .filter(a => toMs(a.createdAt) >= ACTIVITY_CUTOFF_MS)
    .filter(a => includeHidden || !a.hiddenFromCrew)
    .sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));

  const bundles: CrewFeedItem[] = [];
  const pending = new Map<string, CrewFeedItem>();

  for (const activity of visible) {
    const kind = getBundleKind(activity)!;
    const isReview = activity.type === 'movie_reviewed' || !!activity.metadata?.review;
    // Watchlist creations are not bundled: each watchlist is its own social post so engagement
    // (likes/comments) maps cleanly to the underlying watchlist doc.
    const isWatchlist = kind === 'watchlist_created';
    const ts = toMs(activity.createdAt);
    const dateKey = utcDateKey(ts); // YYYYMMDD UTC — deterministic across timezones
    const key =
      isReview ? `review::${activity.id}` :
      isWatchlist ? `watchlist::${activity.watchlistId || (activity as any).entityId || activity.id}` :
      `${activity.uid}::${kind}::${dateKey}`;
    const existing = pending.get(key);

    if (existing && !isReview && !isWatchlist) {
      existing.activities.push(activity);
      existing.count++;
    } else {
      if (existing) bundles.push(finalize(existing));
      pending.set(key, {
        id: activity.id, kind, actorId: activity.uid,
        createdAt: ts, isBundled: false, count: 1,
        activities: [activity],
      });
    }
  }

  for (const item of pending.values()) bundles.push(finalize(item));
  return crossBundleDedupe(bundles.sort((a, b) => b.createdAt - a.createdAt));
}

/**
 * Within a bundle, keep only one activity per movie (the most informative one).
 * Priority: review > rated (has rating) > watched.
 * This prevents the same film from appearing multiple times when a user
 * watched → rated → reviewed the same movie within the bundle window.
 */
function dedupeByMovie(activities: UserActivity[]): UserActivity[] {
  const byMovieId = new Map<string, UserActivity>();
  for (const a of activities) {
    const id = a.movieId;
    if (!id) {
      // No movie ID (e.g. follow event) — keep as-is using activity id
      byMovieId.set(a.id, a);
      continue;
    }
    const existing = byMovieId.get(id);
    if (!existing || activityScore(a) > activityScore(existing)) {
      byMovieId.set(id, a);
    }
  }
  return Array.from(byMovieId.values());
}

function activityScore(a: UserActivity): number {
  if (a.type === 'movie_reviewed' || a.metadata?.review) return 3;
  if (a.rating != null) return 2;
  return 1;
}

/**
 * Filter feed items for the "All posts" tab.
 * Followed users + the current user: all kinds visible (same as Following tab).
 * Everyone else: only watchlist_created, reviews (movie_watched items containing a review),
 * and official crew_post items. Hides low-signal activity (want-to-watch, rated, watched-without-review).
 * Users with the 'test' role are hidden entirely from non-followers.
 */
export function filterFeedItemsForAllTab(
  items: CrewFeedItem[],
  followingIds: string[],
  currentUserUid: string | null | undefined,
  testerUids?: Set<string>,
): CrewFeedItem[] {
  const followedSet = new Set(followingIds);
  return items.filter(item => {
    if (item.kind === 'crew_post') return true;
    // Own hidden posts must not appear in the All tab (user hid them intentionally)
    if (currentUserUid && item.actorId === currentUserUid && item.activities.some(a => a.hiddenFromCrew)) return false;
    const isFollowed = followedSet.has(item.actorId);
    const isSelf = !!currentUserUid && item.actorId === currentUserUid;
    if (isFollowed || isSelf) return true;
    // Test-role users are invisible to non-followers in the All tab
    if (testerUids?.has(item.actorId)) return false;
    // Private profiles: hide all content from non-followers in the All tab.
    // If the profile isn't cached yet, allow through — it will be re-filtered
    // once profiles load and the feed re-renders. Failing open here is acceptable
    // because the feed already relies on post-fetch filtering.
    const authorProfile = getProfileFromCache(item.actorId);
    if (authorProfile?.privacy?.profileVisibility === 'private') return false;
    if (item.kind === 'watchlist_created') return true;
    if (item.kind === 'movie_watched') {
      return item.activities.some(a => a.type === 'movie_reviewed' || !!a.metadata?.review);
    }
    return false;
  });
}

/**
 * Remove movies from `movie_added` bundles when the same actor already has a
 * higher-priority `movie_watched` entry for the same movie on the same UTC day.
 * This prevents "wants to watch" and "watched/rated/reviewed" appearing as
 * separate feed rows for the same movie.
 */
function crossBundleDedupe(items: CrewFeedItem[]): CrewFeedItem[] {
  // Collect actor+movie+day keys covered by watched bundles
  const watchedKeys = new Set<string>();
  for (const item of items) {
    if (item.kind !== 'movie_watched') continue;
    const dateKey = utcDateKey(item.createdAt);
    for (const a of item.activities) {
      if (a.movieId) watchedKeys.add(`${item.actorId}::${a.movieId}::${dateKey}`);
    }
  }

  if (watchedKeys.size === 0) return items;

  return items
    .map(item => {
      if (item.kind !== 'movie_added') return item;
      const dateKey = utcDateKey(item.createdAt);
      const filtered = item.activities.filter(
        a => !a.movieId || !watchedKeys.has(`${item.actorId}::${a.movieId}::${dateKey}`)
      );
      if (filtered.length === 0) return null;
      if (filtered.length === item.activities.length) return item;
      return { ...item, activities: filtered, count: filtered.length, isBundled: filtered.length >= BUNDLE_MIN };
    })
    .filter((item): item is CrewFeedItem => item !== null);
}

function finalize(item: CrewFeedItem): CrewFeedItem {
  const deduped = dedupeByMovie(item.activities);
  return { ...item, activities: deduped, count: deduped.length, isBundled: deduped.length >= BUNDLE_MIN };
}

/**
 * Firestore / protobuf sometimes exposes seconds as a Long: { low, high } (native JSON bridge).
 * String(Object) would be "[object Object]" and break parseInt — yields 0 → "recently" in the feed.
 */
function parseFirestoreIntPart(part: any): number | null {
  if (part == null || part === '') return null;
  if (typeof part === 'number' && Number.isFinite(part)) return Math.trunc(part);
  if (typeof part === 'string') {
    const n = parseInt(part, 10);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof part === 'object' && part !== null && typeof (part as { low?: unknown }).low === 'number') {
    const low = (part as { low: number }).low >>> 0;
    const high = (((part as { high?: number }).high ?? 0) | 0) >>> 0;
    return high * 0x100000000 + low;
  }
  return null;
}

/**
 * Convert Firestore Timestamp / native bridge shapes / ISO strings to epoch ms.
 * Native (@capacitor-firebase/firestore) often uses { seconds, nanoseconds } or { _seconds, _nanoseconds }.
 */
export function toMs(ts: any): number {
  if (ts == null || ts === '') return 0;
  if (typeof ts === 'number') {
    if (!Number.isFinite(ts)) return 0;
    // Heuristic: Unix seconds vs milliseconds
    if (ts > 0 && ts < 1e12) return ts * 1000;
    return ts;
  }
  if (typeof ts === 'string') {
    const trimmed = ts.trim();
    if (!trimmed) return 0;
    const asNum = Number(trimmed);
    if (Number.isFinite(asNum) && trimmed.match(/^\d+$/)) {
      return asNum < 1e12 ? asNum * 1000 : asNum;
    }
    const iso = new Date(trimmed).getTime();
    return Number.isFinite(iso) ? iso : 0;
  }
  if (typeof ts === 'object') {
    if (typeof ts.toMillis === 'function') {
      try {
        const m = ts.toMillis();
        return Number.isFinite(m) ? m : 0;
      } catch {
        return 0;
      }
    }
    if (typeof ts.toDate === 'function') {
      try {
        const m = ts.toDate().getTime();
        return Number.isFinite(m) ? m : 0;
      } catch {
        return 0;
      }
    }
    const secRaw = ts._seconds ?? ts.seconds;
    const sec = parseFirestoreIntPart(secRaw);
    if (sec != null) {
      const nanoRaw = ts._nanoseconds ?? ts.nanoseconds ?? 0;
      const nanoParsed = parseFirestoreIntPart(nanoRaw);
      const n = nanoParsed != null ? nanoParsed : typeof nanoRaw === 'number' ? nanoRaw : parseInt(String(nanoRaw), 10) || 0;
      return sec * 1000 + Math.floor(n / 1e6);
    }
  }
  return 0;
}

function parsePositiveMillisField(v: unknown): number {
  if (v == null || v === '') return 0;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }
  return 0;
}

/** Prefer Firestore Timestamp, fall back to numeric millis (reliable on native when Timestamps are stripped). */
export function timestampOrMillis(data: Record<string, unknown>, tsKey: string, msKey: string): number {
  const fromTs = toMs(data[tsKey]);
  if (fromTs > 0) return fromTs;
  const fromMs = parsePositiveMillisField(data[msKey]);
  return fromMs > 0 ? fromMs : 0;
}

/**
 * UTC YYYYMMDD string for an epoch-ms timestamp. Used as the day-portion of bundle IDs so the
 * same activity hashes to the same bundle regardless of viewer timezone.
 */
export function utcDateKey(ms: number): string {
  const d = new Date(ms);
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0');
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = d.getUTCDate().toString().padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}

/**
 * Stable bundleId for a movie-activity bundle (movie_watched / movie_added only).
 * Returns null for kinds that aren't engagement-bundled (review / watchlist_created / crew_post).
 *
 * Format: `${actorUid}_${kind}_${YYYYMMDD-UTC}` — must match the server-side validation in
 * functions/src/api/socialBundles.ts. Changing the format is a breaking change: existing
 * socialBundles docs would become orphans.
 */
export function bundleIdFromFeedItem(item: CrewFeedItem): string | null {
  if (item.kind !== 'movie_watched' && item.kind !== 'movie_added') return null;
  // Reviews are individual items (not engagement-bundled) — they live under kind=movie_watched
  // but have their own socialPost (kind='review'), so they have no socialBundles doc.
  // Exclude them so scroll/like routing doesn't collide with a same-actor same-day watched bundle.
  const first = item.activities[0];
  if (first && (first.type === 'movie_reviewed' || !!first.metadata?.review)) return null;
  return `${item.actorId}_${item.kind}_${utcDateKey(item.createdAt)}`;
}

export function relativeTime(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return 'recently';
  const diff = Date.now() - ms;
  if (!Number.isFinite(diff) || diff < 0) return 'recently';
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d}d ago`;
  return `${Math.floor(d / 7)}w ago`;
}

// ─── Header text segments ───────────────────────────────────────────────────
/** `profileUserId` on a bold segment makes the name open that user's profile (Crew feed header). */
export interface Seg { text: string; bold?: boolean; profileUserId?: string }

export function headerSegments(item: CrewFeedItem, actorName: string): Seg[] {
  const name = actorName || 'Someone';
  const n = item.count;
  const actor = item.actorId;

  if (item.kind === 'movie_watched') {
    const [a] = item.activities;
    const isReview = a.type === 'movie_reviewed' || !!a.metadata?.review;
    if (isReview) {
      return [{ text: name, bold: true, profileUserId: actor }, { text: ' reviewed' }];
    }
    if (item.isBundled) {
      const hasRating = item.activities.some(x => x.rating != null);
      const verb = hasRating ? 'rated' : 'watched';
      return [{ text: name, bold: true, profileUserId: actor }, { text: ` ${verb} ${n} films` }];
    }
    const verb = a.type === 'movie_rated' || a.rating != null ? 'rated' : 'watched';
    return [{ text: name, bold: true, profileUserId: actor }, { text: ` ${verb}` }];
  }

  if (item.kind === 'movie_added') {
    if (item.isBundled) {
      return [{ text: name, bold: true, profileUserId: actor }, { text: ` wants to watch ${n} films` }];
    }
    return [{ text: name, bold: true, profileUserId: actor }, { text: ' wants to watch' }];
  }

  if (item.kind === 'watchlist_created') {
    if (item.isBundled) {
      return [{ text: name, bold: true, profileUserId: actor }, { text: ` created ${n} watchlists` }];
    }
    return [{ text: name, bold: true, profileUserId: actor }, { text: ' created a new watchlist' }];
  }

  if (item.kind === 'crew_post' && item.crewPost?.title) {
    return [{ text: item.crewPost.title, bold: true }];
  }

  return [{ text: name, bold: true, profileUserId: actor }];
}

function normalizeCrewPostLinks(raw: unknown): CrewPostLinks {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const arr = (v: unknown): string[] | undefined => {
    if (!Array.isArray(v)) return undefined;
    const out = v.map(x => String(x).trim()).filter(Boolean);
    return out.length ? out : undefined;
  };
  return {
    watchlistIds: arr(o.watchlistIds),
    movieIds: arr(o.movieIds),
    reviewIds: arr(o.reviewIds),
    userIds: arr(o.userIds),
    personIds: arr(o.personIds),
  };
}

/** Map a Firestore `crewPosts` document into a feed row (always shown as Watchlist Crew). */
export function crewPostDocToFeedItem(
  docId: string,
  data: Record<string, unknown>,
  nowMs: number = Date.now()
): CrewFeedItem {
  const title = typeof data.title === 'string' ? data.title : '';
  const subtitle = typeof data.subtitle === 'string' ? data.subtitle : undefined;
  const pinExp = timestampOrMillis(data, 'pinExpiresAt', 'pinExpiresAtMillis');
  const isPinned = pinExp > nowMs;
  return {
    id: `crewpost_${docId}`,
    kind: 'crew_post',
    actorId: WATCHLIST_CREW_UID,
    createdAt: timestampOrMillis(data, 'createdAt', 'createdAtMillis'),
    isBundled: false,
    count: 1,
    activities: [],
    crewPost: {
      title,
      subtitle,
      links: normalizeCrewPostLinks(data.links),
      isPinned,
      pinExpiresAtMs: isPinned ? pinExp : undefined,
      postDocId: docId,
      authorUid: typeof data.authorUid === 'string' ? data.authorUid : undefined,
      likeCount: typeof data.likeCount === 'number' ? data.likeCount : 0,
      likedBy: Array.isArray(data.likedBy) ? (data.likedBy as string[]) : [],
      commentCount: typeof data.commentCount === 'number' ? data.commentCount : 0,
    },
  };
}

/** Sort crew post docs: active pins first (newest pin first), then by createdAt desc. */
export function sortCrewPostDocsForFeed(
  docs: Array<{ id: string; data: Record<string, unknown> }>,
  nowMs: number = Date.now()
): Array<{ id: string; data: Record<string, unknown> }> {
  const decorated = docs.map(d => {
    const pinExp = timestampOrMillis(d.data, 'pinExpiresAt', 'pinExpiresAtMillis');
    const pinnedAt = timestampOrMillis(d.data, 'pinnedAt', 'pinnedAtMillis');
    const isPinned = pinExp > nowMs;
    return {
      d,
      isPinned,
      pinnedAtMs: pinnedAt || 0,
      createdAt: timestampOrMillis(d.data, 'createdAt', 'createdAtMillis'),
    };
  });
  decorated.sort((a, b) => {
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
    if (a.isPinned && b.isPinned) return b.pinnedAtMs - a.pinnedAtMs;
    return b.createdAt - a.createdAt;
  });
  return decorated.map(x => x.d);
}

function isActivePin(item: CrewFeedItem, nowMs: number): boolean {
  return item.kind === 'crew_post' && (item.crewPost?.pinExpiresAtMs ?? 0) > nowMs;
}

/**
 * Pinned official crew posts first (in array order), then activity + unpinned crew by recency.
 */
export function mergeCrewFeedItems(
  bundles: CrewFeedItem[],
  crewPosts: CrewFeedItem[],
  nowMs: number = Date.now()
): CrewFeedItem[] {
  const pinned = crewPosts.filter(c => isActivePin(c, nowMs));
  const unpinnedCrew = crewPosts.filter(c => !isActivePin(c, nowMs));
  const rest = [...bundles, ...unpinnedCrew].sort((a, b) => b.createdAt - a.createdAt);
  return [...pinned, ...rest];
}
